// SHI-528 — how many days an order is PRODUCED for vs how many it DECLARES on the label.
//
// These are deliberately different numbers.
//
//   PRODUCED  = 31 per month. We make one spare pouch every 30 days so a breakage or a
//               short fill never leaves the customer short.
//   DECLARED  = 30 per month. Net quantity is a regulated declaration, so the label states
//               the nominal supply. The spare is margin: the bag always holds at least
//               what the label claims, never less.
//
// So a 3-month order is produced as 93 pouches and labelled as 90 days' worth; a 5-month
// order is 155 produced, 150 declared.
//
// This replaces a hardcoded binary that lived in four label files:
//
//     const total = order?.order_months === '3' ? sum * 93 : sum * 31;
//
// which was right only for 1- and 3-month orders and silently wrong for everything else —
// a 2-month order declared 31 days instead of 60, and a riise 90-day one-off declared 31
// instead of 90, roughly a third of what was in the bag.

// SHI-528 — does this order have dosed pouches at all?
//
// Omega-3 here is the soft-gel bottle supplied alongside the pouches, not the powder dosed
// into them. An Omega-3-ONLY order has no pouches, so it has no pouch count to show and no
// ingredient label to print — printing one would emit a blank page with a 0g net quantity.
// An order with a formula PLUS an Omega-3 add-on is a normal dosed order and keeps both.
//
// Two shapes, because riise and manual/external record it differently.

// riise: decided by the order's own line items.
export const isOmega3OnlyRiise = (orderItems) => {
  const items = Array.isArray(orderItems) ? orderItems : [];
  const hasOmega3 = items.some((i) => i?.product_type === 'omega-3');
  const hasOther = items.some((i) => i?.product_type && i.product_type !== 'omega-3');
  return hasOmega3 && !hasOther;
};

// manual / external: an `omega3` column plus the formula JSON.
export const isOmega3OnlyOrder = (order) => {
  const o3 = String(order?.omega3 ?? '').trim();
  if (!o3 || o3 === '0') return false;
  // Random pouches ARE pouches — they just have no fixed formula.
  if (order?.random_pouches) return false;
  let ings = order?.ingredients;
  if (typeof ings === 'string') {
    try { ings = JSON.parse(ings || '[]'); } catch (e) { ings = []; }
  }
  return !Array.isArray(ings) || ings.length === 0;
};

export const PRODUCED_DAYS_PER_MONTH = 31;
export const DECLARED_DAYS_PER_MONTH = 30;

// Neutral-pellet filler (SHI-423). Every pouch is topped up to this weight when the
// formula alone is lighter, so the pouch is always physically filled.
//
// ⚠ This MIRRORS `NEUTRAL_PELLETS_TARGET_MG` in riise-ops/src/utils/filler.js. The two
// must move together — ops doses to that number, and this declares it. If the target ever
// changes, change it in BOTH repos or the label will misstate what is in the bag.
export const POUCH_TARGET_MG = 1000;

// The real weight of one daily pouch: the formula, plus enough neutral pellets to reach
// the target. This is what net quantity has to be built from — the pellets are physically
// in the pouch and consumed, so they are part of the contents. A sparse formula is mostly
// filler: a one-ingredient order weighing 41.7mg/day ships a 1000mg pouch, 96% pellets.
//
// An empty pouch (an Omega-3-only order has no dosed pouch at all) gets nothing.
export const pouchWeightMg = (formulaPerDayMg) => {
  const w = Number(formulaPerDayMg) || 0;
  if (w <= 0) return 0;
  return Math.max(w, POUCH_TARGET_MG);
};

// riise `usersubscriptions.duration` is a plan code, not a month count:
//   3 = 1-month sub, 6 = 3-month sub, 30 = 30-day one-off, 90 = 90-day one-off.
// Mirrors MONTHS_TO_PREPARE in riise-server's operator.controller.
const RIISE_DURATION_TO_MONTHS = { 1: 1, 2: 2, 3: 1, 6: 3, 30: 1, 90: 3 };

export const monthsFromRiiseDuration = (duration) =>
  RIISE_DURATION_TO_MONTHS[parseInt(duration, 10)] || 1;

// Manual and external orders carry a literal month count in `order_months`.
export const monthsFromOrderMonths = (orderMonths) => parseInt(orderMonths, 10) || 1;

// Pouches actually made. An explicit dosing-days override REPLACES the derivation — that
// value is already the literal pouch count, so no spare is added on top of it.
export const producedDays = (months, dosingDaysOverride) => {
  const override = parseInt(dosingDaysOverride, 10);
  if (Number.isInteger(override) && override > 0) return override;
  return months * PRODUCED_DAYS_PER_MONTH;
};

// Days the label declares. For a normal run this is the nominal supply (months x 30).
//
// For an overridden run the operator has stated the exact number of pouches to make — a
// partial re-make of 11 replacement pouches is 11, not "11 minus a spare". Declaring that
// count is exact and, crucially, never more than the bag holds.
export const declaredDays = (months, dosingDaysOverride) => {
  const override = parseInt(dosingDaysOverride, 10);
  if (Number.isInteger(override) && override > 0) return override;
  return months * DECLARED_DAYS_PER_MONTH;
};

// Convenience wrappers for the two order shapes the label components see.
export const declaredDaysForOrderMonths = (order) =>
  declaredDays(monthsFromOrderMonths(order?.order_months), order?.dosing_days);

export const declaredDaysForRiiseDuration = (duration) =>
  declaredDays(monthsFromRiiseDuration(duration), null);
